// src/components/Login.js
import React, { useState } from 'react';
import { apiFetch } from '../apiFetch';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,

} from 'firebase/auth';
import { auth } from '../firebase-config';
import './Login.css';

const ROLE_ID_KEYS = {
  agent: 'agentId',
  scout: 'scoutId',
  club: 'clubId',
  player: 'playerId',
};

function routeAfterLogin(navigate) {
  const role =
    (localStorage.getItem('userRole') ||
      localStorage.getItem('role') ||
      '').toLowerCase();

  const idKey = ROLE_ID_KEYS[role];
  const hasId = idKey ? !!localStorage.getItem(idKey) : false;

  if (role === 'admin') {
    navigate('/admin-dashboard', { replace: true });
    return;
  }

  if (!role || !hasId) {
    navigate('/complete-registration', { replace: true });
    return;
  }

  switch (role) {
    case 'player':
      navigate('/player-dashboard', { replace: true });
      break;
    case 'scout':
      navigate('/scout-dashboard', { replace: true });
      break;
    case 'club':
      navigate('/club-dashboard', { replace: true });
      break;
    case 'agent':
    default:
      navigate('/agent-dashboard', { replace: true });
      break;
  }
}



const hydrateUserFromBackend = async () => {
  const user = auth.currentUser;
  if (!user) return { error: true };

  try {
    const response = await apiFetch("/me", {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    // ✅ NORMAL STATE: user has no role yet
    if (response.status === 404) {
      return { needsRegistration: true };
    }

    if (!response.ok) {
      return { error: true };
    }

    const data = await response.json();

    // Clear previous role state
    ["role", "userRole", "agentId", "scoutId", "clubId", "playerId", "playerProfileSlug"].forEach((k) =>
      localStorage.removeItem(k)
    );


    localStorage.setItem("role", data.role);
    localStorage.setItem("userRole", data.role);

    if (data.roleId) {
      localStorage.setItem(`${data.role}Id`, String(data.roleId));
    }

    // ✅ Store slug for slug-based public profile URLs
    if (data.role === "player" && data.profileSlug) {
      localStorage.setItem("playerProfileSlug", data.profileSlug);
    }


    return { hasProfile: true };
  } catch {
    return { error: true };
  }
};



const checkDisabledStatus = async (firebaseUid) => {
  try {
    const res = await apiFetch(`/api/all-users/status/${firebaseUid}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    // If not found, treat as not disabled (safe default)
    if (!res.ok) {
      return { isDisabled: false };
    }

    const data = await res.json();
    return { isDisabled: Boolean(data.isDisabled) };
  } catch (err) {
    console.error("❌ checkDisabledStatus error:", err);
    return { isDisabled: false };
  }
};




const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [resetError, setResetError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrorMessage('');
    setEmailError('');
    setResetError('');

    if (!email) {
      setEmailError('Please enter your email');
      return;
    }

    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const user = cred.user;

      // 🔒 Refresh auth state
      await user.reload();

      // 🚫 Block unverified users
      if (!user.emailVerified) {
        await auth.signOut();
        setErrorMessage('Please verify your email address before logging in.');
        setLoading(false);
        return;
      }

      const uid = cred.user?.uid;

      // ✅ NEW: Block disabled users (from AllUsers table)
      if (uid) {
        const { isDisabled } = await checkDisabledStatus(uid);

        // store in localStorage for App.js gate()
        localStorage.setItem("isDisabled", String(isDisabled));

        if (isDisabled) {
          await auth.signOut();
          setLoading(false);
          navigate("/account-disabled", { replace: true });
          return;
        }
      }



      const lastUid = localStorage.getItem('authUid');

      if (uid && lastUid && uid !== lastUid) {
        ['role', 'userRole', 'agentId', 'scoutId', 'clubId', 'playerId', 'playerProfileSlug', 'isDisabled']
          .forEach(k => localStorage.removeItem(k));
      }


      if (uid) localStorage.setItem('authUid', uid);

      
      const result = await hydrateUserFromBackend();

      // 🟡 User is logged in but has NOT completed registration
      if (result?.needsRegistration) {
        setLoading(false);
        navigate('/complete-registration', { replace: true });
        return;
      }

      // 🔴 Actual error
      if (result?.error) {
        setErrorMessage('Unable to load user profile. Please try again.');
        setLoading(false);
        return;
      }

      // ✅ User has role → go to dashboard
      setLoading(false);
      routeAfterLogin(navigate);



    } catch (error) {
      setErrorMessage('Invalid email or password. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setResetError('');
    setErrorMessage('');

    if (!email) {
      setResetError('Please enter your email address before proceeding.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
      alert('Password reset email sent!');
    } catch {
      setResetError('Error sending password reset email.');
    }
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit}>

        <img
          src={`${process.env.PUBLIC_URL}/images/VSN-Icon-White-Cropped.png`}
          alt="VSN"
          className="login-logo"
        />

        <h2>Welcome back</h2>

        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          disabled={loading}
        />
        {emailError && <p className="error-message">{emailError}</p>}

        <label>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          disabled={loading}
        />
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        <button type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Login'}
        </button>

        <p>
          Don’t have an account?
          <button
            type="button"
            className="auth-link"
            onClick={() => navigate('/register')}
          >
            Register
          </button>
        </p>

        <button
          type="button"
          onClick={handleForgotPassword}
          className="forgot-password-button"
          disabled={loading}
        >
          Forgot Password? Reset Here
        </button>

        {resetError && <p className="error-message">{resetError}</p>}
      </form>
    </div>
  );
};

export default Login;













